/**
 * Hook for centralized permission management
 * 
 * Provides a clean interface to check user permissions based on their role.
 * This centralizes all permission logic in one place for easier maintenance.
 */

import { useAuth } from '../contexts/AuthContext';
import type { UserRole } from '../types/base';

export interface Permissions {
  // Company management (Superadmin only)
  canManageCompanies: boolean;
  
  // User management (Admin and Superadmin)
  canManageUsers: boolean;
  
  // Project management (Admin and Superadmin)
  canCreateProject: boolean;
  canEditProject: boolean;
  canDeleteProject: boolean;
  canChangeProjectStatus: boolean;
  
  // Client management (Admin and Superadmin)
  canManageClients: boolean;
  
  // Category management (Admin and Superadmin)
  canManageCategories: boolean;
  
  // Expense management
  canCreateExpense: boolean;
  canEditOwnExpense: boolean;
  canEditAnyExpense: boolean;
  canDeleteOwnExpense: boolean;
  canDeleteAnyExpense: boolean;
  canViewAllExpenses: boolean;
  
  // Reports
  canViewAllReports: boolean;
  canExportReports: boolean;
  
  // Settings
  canManageCompanySettings: boolean;
  
  // Role checks
  isSuperAdmin: boolean;
  isAdmin: boolean;
  isColaborador: boolean;
  
  // Helper to check if user can edit a specific expense
  canEditExpense: (expenseUserId: string) => boolean;
  canDeleteExpense: (expenseUserId: string) => boolean;
}

export const usePermissions = (): Permissions => {
  const { user } = useAuth();
  const role: UserRole | undefined = user?.role;
  
  const isSuperAdmin = role === 'superadmin';
  const isAdmin = role === 'admin';
  const isColaborador = role === 'colaborador';
  
  // Helper arrays for common permission groups
  const adminRoles: UserRole[] = ['admin', 'superadmin'];
  const allRoles: UserRole[] = ['colaborador', 'admin', 'superadmin'];
  
  return {
    // Company management
    canManageCompanies: isSuperAdmin,
    
    // User management
    canManageUsers: role ? adminRoles.includes(role) : false,
    
    // Project management
    canCreateProject: role ? adminRoles.includes(role) : false,
    canEditProject: role ? adminRoles.includes(role) : false,
    canDeleteProject: role ? adminRoles.includes(role) : false,
    canChangeProjectStatus: role ? adminRoles.includes(role) : false,
    
    // Client management
    canManageClients: role ? adminRoles.includes(role) : false,
    
    // Category management
    canManageCategories: role ? adminRoles.includes(role) : false,
    
    // Expense management
    canCreateExpense: role ? allRoles.includes(role) : false,
    canEditOwnExpense: role ? allRoles.includes(role) : false,
    canEditAnyExpense: role ? adminRoles.includes(role) : false,
    canDeleteOwnExpense: role ? allRoles.includes(role) : false,
    canDeleteAnyExpense: role ? adminRoles.includes(role) : false,
    canViewAllExpenses: true, // All users can view all expenses
    
    // Reports
    canViewAllReports: true, // All users can view all reports
    canExportReports: role ? adminRoles.includes(role) : false,
    
    // Settings
    canManageCompanySettings: role ? adminRoles.includes(role) : false,
    
    // Role checks
    isSuperAdmin,
    isAdmin,
    isColaborador,
    
    // Helper functions
    canEditExpense: (expenseUserId: string) => {
      if (!user) return false;
      // Admin/Superadmin can edit any expense
      if (role && adminRoles.includes(role)) return true;
      // Colaborador can only edit their own expenses
      return user.userId === expenseUserId;
    },
    
    canDeleteExpense: (expenseUserId: string) => {
      if (!user) return false;
      // Admin/Superadmin can delete any expense
      if (role && adminRoles.includes(role)) return true;
      // Colaborador can only delete their own expenses
      return user.userId === expenseUserId;
    },
  };
};
